import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { FormSpy } from "react-final-form";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import api from "../../api";
import { add } from "date-fns";

import { FormContext } from "../../context/form-context";
import { setSelectedBaby } from '../../actions/ui';
import {
  measurementRange,
  composeValidators,
  maxLength,
  required,
  valueIsNumber,
} from "../../helpers/formValidators";

import { addDateAndTimeFields } from "./Duration";

import {
  FormLabel,
  FormInputHolder,
  FormInput,
  FormSelect,
  FormRadio,
  FormIconChooser,
  BaseForm,
  FormTimeInput,
  FormDateInput,
} from "../form";

import ErrorMessage from "../ErrorMessage/ErrorMessage";
import { PrimaryButton, SecondaryButton } from "../buttons";
import babyIcons from "../icons/babyIcons";
import { Error } from "../form/BaseForm";
import { OnChange } from "react-final-form-listeners";

const GrowthForm = (props) => {
  const {
    selectedProfile,
    babies,
    profileOptions,
    growthRecordsPatch,
    growthRecordsPost,
    accountId,
    handleRecords,
    showUnSavedModal,
    hideTrackerModals,
    showFirstTimeSavingModal,
    showSaveEntryModal,
    growthModalShown = false,
    selectedBabyId = null
  } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeDashboardBabyId = useSelector(state => state.ui.selectedBabyId);

  const [initialValues, setInitialValues] = useState(null);
  const formContext = useContext(FormContext);

  const defaultValues = {
    babyId: selectedBabyId,
    providerId: selectedProfile.id,
    weight: "100.00",
    bodyLength: "10.00",
    headCircumference: "0.00",
    date: new Date(),
    time: new Date(),
  };

  useEffect(() => {
    if (!growthModalShown) {
      setInitialValues(null);
      return;
    }

    if (formContext.formValues && Object.keys(formContext.formValues).length > 0) {
      setInitialValues({ ...formContext.formValues });
      return;
    }

    const latestRecord = handleRecords('growth', selectedBabyId) || {};

    setInitialValues({
      ...defaultValues,
      ...latestRecord,
      babyId: selectedBabyId,
      date: latestRecord.date ? new Date(latestRecord.date) : new Date(),
      time: latestRecord.date ? new Date(latestRecord.date) : new Date(),
      weight: parseFloat(latestRecord.weight || defaultValues.weight).toFixed(2),
      bodyLength: parseFloat(latestRecord.bodyLength || defaultValues.bodyLength).toFixed(2),
      headCircumference: parseFloat(latestRecord.headCircumference || defaultValues.headCircumference).toFixed(2),
    });
  }, [growthModalShown, selectedBabyId]);

  const toggleWarningModal = () => {
    return formContext.isDirty ? showUnSavedModal() : hideTrackerModals();
  };

  const onSubmit = (values) => {
    const recordBody = {
      ...values,
      providerId: values.providerId === "other" ? null : values.providerId,
      otherProvider: values.providerId !== "other" ? null : values.otherProvider,
      date: addDateAndTimeFields(values.date, values.time),
    };

    const originalBabyId = initialValues?.babyId;
    const newBabyId = values.babyId || originalBabyId;

    const handleSuccess = (data, shouldNavigate = false) => {
      if (data) {
        dispatch(api.actions.growthRecordsList({ accountId, babyId: originalBabyId }));
        
        if (newBabyId !== originalBabyId) {
          dispatch(api.actions.growthRecordsList({ accountId, babyId: newBabyId }));
        }
        
        dispatch(api.actions.growthRecordsLatest({ accountId }));
        hideTrackerModals();

        if (shouldNavigate) {
          dispatch(setSelectedBaby(newBabyId));
          navigate('/dashboard/growth');
        }
      }
    };

    if (values.id) {
      return growthRecordsPatch(
        { accountId: accountId, babyId: originalBabyId, id: values.id },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => handleSuccess(data, false))
        .catch(() => <ErrorMessage />);
    } else {
      return growthRecordsPost(
        { accountId: accountId, babyId: newBabyId },
        { body: JSON.stringify(recordBody) }
      )
        .then((data) => handleSuccess(data, true))
        .catch(() => <ErrorMessage />);
    }
  };

  const onSave = (values) => {
    if (!showFirstTimeSavingModal) {
      onSubmit(values);
    } else {
      formContext.setValues(values);
      formContext.setFunctions({ onSubmit });
      return showSaveEntryModal();
    }
  };

  if (!growthModalShown || !initialValues) return null;

  return (
    <BaseForm
      key={initialValues.id || 'new'}
      onSubmit={onSave}
      initialValues={initialValues}
      className="activity-tracker__form"
    >
      <h2>Track Growth</h2>

      {babies && babies.length > 1 && !initialValues?.id && (
        <React.Fragment>
          <FormIconChooser>
            {babies.map((baby) => (
              <FormRadio
                key={baby.id}
                value={baby.id}
                name="babyId"
                imageSrc={babyIcons[baby.icon]}
                validate={required}
              >
                {baby.name}
              </FormRadio>
            ))}
          </FormIconChooser>

          <OnChange name="babyId">
            {(value, previous) => {
              if (value === previous) return;
              const latestRecord = handleRecords('growth', value) || {};
              setInitialValues({
                ...defaultValues,
                ...latestRecord,
                babyId: value,
                date: latestRecord.date ? new Date(latestRecord.date) : new Date(),
                time: latestRecord.date ? new Date(latestRecord.date) : new Date(),
                weight: parseFloat(latestRecord.weight || defaultValues.weight).toFixed(2),
                bodyLength: parseFloat(latestRecord.bodyLength || defaultValues.bodyLength).toFixed(2),
                headCircumference: parseFloat(latestRecord.headCircumference || defaultValues.headCircumference).toFixed(2),
              });
            }}
          </OnChange>

          <Error name="babyId" />
        </React.Fragment>
      )}

      {babies && initialValues?.id && (() => {
        const editBaby = babies.find(b => b.id === initialValues.babyId);
        return editBaby ? (
          <FormIconChooser>
            <label className="form-radio form-radio--checked">
              <div className="form-radio__image-container">
                <img src={babyIcons[editBaby.icon]} alt={editBaby.name} className="form-radio__image" width="2.4rem" height="2.4rem" />
              </div>
              <span className="form-radio__label">{editBaby.name}</span>
            </label>
          </FormIconChooser>
        ) : null;
      })()}

      <FormLabel htmlFor="providerId">Provider</FormLabel>
      <FormSelect
        name="providerId"
        options={profileOptions}
        validate={required}
        includeOther
        otherName="otherProvider"
      />

      <FormInputHolder>
        <div>
          <FormInput
            name="weight"
            maxLength={7}
            validate={composeValidators(
              measurementRange([100.0, 8000.0]),
              maxLength(8),
              valueIsNumber
            )}
            labelRenderer={() => (
              <FormLabel htmlFor="weight" note="Optional">
                Baby Weight
              </FormLabel>
            )}
            type="number"
            measurement="g"
            format={(value) => (value ? parseFloat(value).toFixed(2) : "0.00")}
            formatOnBlur
          />
        </div>
      </FormInputHolder>

      <FormInputHolder>
        <div>
          <FormInput
            name="bodyLength"
            maxLength={6}
            validate={composeValidators(
              measurementRange([10, 100]),
              maxLength(7),
              valueIsNumber
            )}
            labelRenderer={() => (
              <FormLabel htmlFor="bodyLength" note="Optional">
                Baby Length
              </FormLabel>
            )}
            type="number"
            measurement={"cm"}
            format={(value) => (value ? parseFloat(value).toFixed(2) : "0.00")}
            formatOnBlur
          />
        </div>
      </FormInputHolder>

      <FormInputHolder>
        <div>
          <FormInput
            name="headCircumference"
            maxLength={6}
            validate={composeValidators(
              measurementRange([5, 100]),
              maxLength(7),
              valueIsNumber
            )}
            labelRenderer={() => (
              <FormLabel htmlFor="headCircumference" note="Optional">
                Head Circumference
              </FormLabel>
            )}
            measurement={"cm"}
            type="number"
            format={(value) => (value ? parseFloat(value).toFixed(2) : "0.00")}
            formatOnBlur
          />
        </div>
      </FormInputHolder>

      <FormInputHolder>
        <FormDateInput
          name="date"
          validate={required}
          labelRenderer={() => <FormLabel htmlFor="date">Date</FormLabel>}
          dateFormat="MMM dd, Y"
        />

        <FormTimeInput
          name="time"
          validate={required}
          labelRenderer={() => <FormLabel htmlFor="time">Time</FormLabel>}
        />
      </FormInputHolder>

      <FormSpy subscription={{ submitting: true }}>
        {({ submitting }) => (
          <FormInputHolder>
            <SecondaryButton
              type="button"
              label="Cancel"
              onClick={toggleWarningModal}
            />
            <PrimaryButton type="submit" label="Save" disabled={submitting} />
          </FormInputHolder>
        )}
      </FormSpy>

      <FormSpy
        subscription={{ dirty: true, visited: true, values: true }}
        onChange={({ dirty, visited, values }) => {
          setTimeout(() => {
            if (dirty) {
              formContext.setValues(values);
              const actuallyDirty = Object.values(visited).some(Boolean);
              actuallyDirty ? formContext.setDirty() : formContext.notDirty();
            }
          }, 0);
        }}
      />
    </BaseForm>
  )
};

GrowthForm.propTypes = {
  babies: PropTypes.arrayOf(PropTypes.object),
  accountId: PropTypes.string,
  growthRecordsPost: PropTypes.func,
  growthRecordsPatch: PropTypes.func,
  showUnSavedModal: PropTypes.func,
  handleRecords: PropTypes.func,
  hideTrackerModals: PropTypes.func,
  profileOptions: PropTypes.arrayOf(PropTypes.object),
  selectedProfile: PropTypes.object,
  showFirstTimeSavingModal: PropTypes.bool,
  showSaveEntryModal: PropTypes.func,
  growthModalShown: PropTypes.bool,
  selectedBabyId: PropTypes.string
};

export default GrowthForm;